import '../../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const safeStr = (v, d = '') =>
  typeof v === 'string' && v.trim() ? v.trim() : d;

// FIX: use the same fallback chain as the rest of the codebase (was reading
// only `performance_score`, missing sessions that only have `overall_score`)
const safeScore = (it) => {
  const v =
    it?.performance_score ??
    it?.overall_score ??
    it?.final_score ??
    it?.score ??
    it?.results?.performance_score ??
    it?.results?.overall_score ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const prettyLabel = (v) =>
  String(v || '—')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === 'object') return v;
  if (typeof v !== 'string') return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function meta(it) {
  const cfg = it?.configuration || it?.config || {};
  return {
    type: safeStr(it?.interview_type || cfg?.interview_type || 'unknown'),
    role: safeStr(
      it?.selected_role || cfg?.selected_role || it?.role || ''
    ),
    industry: safeStr(
      it?.selected_industry || cfg?.selected_industry || it?.industry || ''
    ),
    difficulty: safeStr(
      it?.ai_difficulty || cfg?.ai_difficulty || it?.difficulty || ''
    ),
    persona: safeStr(
      it?.ai_persona_name || cfg?.ai_persona_name || it?.persona || ''
    ),
    company_type: safeStr(
      it?.selected_company_type ||
        cfg?.selected_company_type ||
        it?.company_type ||
        ''
    ),
    format: safeStr(
      it?.selected_format || cfg?.selected_format || it?.format || ''
    ),
    duration: safeNum(
      it?.duration ?? cfg?.duration ?? it?.duration_minutes ?? cfg?.duration_minutes,
      null
    ),
    tone: safeStr(it?.ai_tone || cfg?.ai_tone || ''),
    followups: it?.enable_follow_ups ?? cfg?.enable_follow_ups,
    star_enforced:
      it?.star_method_enforcement ?? cfg?.star_method_enforcement,
    follow_intensity: it?.follow_up_intensity ?? cfg?.follow_up_intensity,
  };
}

function normalizeArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string')
    return v.split('\n').map((x) => x.trim()).filter(Boolean);
  if (typeof v === 'object') return Object.values(v);
  return [];
}

function parseFeedback(raw) {
  if (!raw) return { general: '', detailed: '', strengths: [], weakest: [] };
  const obj = typeof raw === 'string' ? null : raw;
  const maybe = obj || parseMaybeJson(raw);

  if (maybe && typeof maybe === 'object') {
    return {
      general: safeStr(
        maybe.general || maybe.summary || maybe.overview || ''
      ),
      detailed: safeStr(maybe.detailed_feedback || maybe.detailed || ''),
      strengths: normalizeArray(maybe.strengths),
      weakest: normalizeArray(maybe.weakest_areas || maybe.weaknesses),
    };
  }
  return {
    general: typeof raw === 'string' ? raw : '',
    detailed: '',
    strengths: [],
    weakest: [],
  };
}

// Pull deep performance signals from a session (PastInterviews-grade detail)
function readSignals(it) {
  const r = it?.results || it || {};
  return {
    speaking_wpm: r.speaking_pace_wpm ?? it?.speaking_pace_wpm ?? null,
    filler_words: r.filler_word_count ?? it?.filler_word_count ?? null,
    eye_contact:
      r.eye_contact_percentage ?? it?.eye_contact_percentage ?? null,
    star_used:
      r.star_method_usage_count ?? it?.star_method_usage_count ?? null,
    total_questions:
      r.total_questions_asked ?? it?.total_questions_asked ?? null,
  };
}

function readSuggestions(it) {
  const r = it?.results || it || {};
  return normalizeArray(
    r.improvement_suggestions ??
      r.suggestions ??
      it?.improvement_suggestions ??
      it?.suggestions
  );
}

const palette = [
  'rgba(99,102,241,0.78)',
  'rgba(56,189,248,0.75)',
  'rgba(52,211,153,0.72)',
  'rgba(245,158,11,0.66)',
  'rgba(244,63,94,0.62)',
  'rgba(167,139,250,0.62)',
];

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-stretch justify-end">
        <div className="w-full max-w-[680px] h-[100svh] bg-[#070a10]/92 border-l border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_30px_120px_-55px_rgba(0,0,0,0.95)] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

// PastInterviews-style mini metric card for the session modal
function MetricMini({ label, value, suffix = '', goodFrom = 80 }) {
  const v = value == null ? null : Number(value);
  const ok = v != null && Number.isFinite(v) && v >= goodFrom;
  return (
    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3">
      <div className="text-xs text-white/55">{label}</div>
      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
        <div className="text-lg font-semibold text-white tabular-nums">
          {v == null ? '—' : `${Math.round(v)}${suffix}`}
        </div>
        <div
          className={`text-[11px] ${
            v == null
              ? 'text-white/35'
              : ok
                ? 'text-emerald-300/80'
                : 'text-amber-300/80'
          }`}
        >
          {v == null ? 'n/a' : ok ? 'strong' : 'needs work'}
        </div>
      </div>
    </div>
  );
}

const DetailedBreakdown = ({
  sessions = [],
  statistics = null,
  skillAnalysis = [],
  typeDistribution = [],
  timeOfDay = [],
  onDeleteSession, // FIX: optional callback so the parent can delete the row
}) => {
  const [tab, setTab] = useState('sessions');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('completed'); // FIX: PastInterviews-style status filter
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const all = sessions || [];

  // FIX: only use status === completed when statusFilter says so. Old code
  // hard-coded `status === 'completed'` so charts/sessions list never showed
  // anything else.
  const visibleByStatus = useMemo(() => {
    if (statusFilter === 'all') return all;
    return all.filter(
      (s) => String(s?.status || '').toLowerCase() === statusFilter
    );
  }, [all, statusFilter]);

  // For aggregations (charts), keep "completed" semantics.
  const completed = useMemo(
    () => all.filter((s) => String(s?.status || '').toLowerCase() === 'completed'),
    [all]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visibleByStatus.filter((it) => {
      const m = meta(it);
      const typeOk =
        typeFilter === 'all' ? true : String(m.type).toLowerCase() === typeFilter;
      if (!typeOk) return false;
      if (!q) return true;
      const hay =
        `${m.role} ${m.industry} ${m.type} ${m.format} ${m.difficulty} ${m.persona}`.toLowerCase();
      return hay.includes(q);
    });
  }, [visibleByStatus, query, typeFilter]);

  const typeOptions = useMemo(() => {
    const opts = new Set(
      (typeDistribution || [])
        .map((t) => String(t.key || t.name || '').toLowerCase())
        .filter(Boolean)
    );
    all.forEach((it) => {
      const t = String(meta(it).type).toLowerCase();
      if (t && t !== 'unknown') opts.add(t);
    });
    return ['all', ...Array.from(opts)];
  }, [typeDistribution, all]);

  const scoreHistogram = useMemo(() => {
    const buckets = [
      { bucket: '0–59', min: 0, max: 59, count: 0 },
      { bucket: '60–69', min: 60, max: 69, count: 0 },
      { bucket: '70–79', min: 70, max: 79, count: 0 },
      { bucket: '80–89', min: 80, max: 89, count: 0 },
      { bucket: '90–100', min: 90, max: 100, count: 0 },
    ];

    completed.forEach((it) => {
      const s = safeScore(it);
      if (s == null) return;
      const b = buckets.find((x) => s >= x.min && s <= x.max);
      if (b) b.count += 1;
    });

    return buckets;
  }, [completed]);

  const byTypeScore = useMemo(() => {
    const map = {};
    completed.forEach((it) => {
      const t = String(meta(it).type || 'unknown').toLowerCase();
      if (!t || t === 'unknown') return;
      if (!map[t]) map[t] = { type: t, sum: 0, count: 0 };
      const s = safeScore(it);
      if (s == null) return;
      map[t].sum += s;
      map[t].count += 1;
    });

    return Object.values(map)
      .map((x) => ({
        type: prettyLabel(x.type),
        avg: x.count ? Math.round((x.sum / x.count) * 10) / 10 : 0,
        sessions: x.count,
      }))
      .sort((a, b) => b.sessions - a.sessions);
  }, [completed]);

  // FIX: hide time-of-day buckets that have NO sessions so the chart isn't
  // 2 visible bars + 2 empty rectangles.
  const timeOfDayClean = useMemo(
    () => (timeOfDay || []).filter((b) => safeNum(b.sessions, 0) > 0),
    [timeOfDay]
  );

  const topSignals = useMemo(() => {
    const last = [...completed]
      .filter((x) => x?.completed_at)
      .sort(
        (a, b) =>
          new Date(b.completed_at).getTime() -
          new Date(a.completed_at).getTime()
      )
      .slice(0, 10);

    const strengths = {};
    const weakest = {};
    last.forEach((it) => {
      const fb = parseFeedback(it?.feedback ?? it?.results?.feedback);
      // also pick up direct fields
      const directS = normalizeArray(
        it?.strengths ?? it?.results?.strengths
      );
      const directW = normalizeArray(
        it?.weakest_areas ?? it?.results?.weakest_areas
      );
      [...fb.strengths, ...directS].forEach((s) => {
        const key = typeof s === 'string' ? s : s?.title;
        if (!key) return;
        strengths[key] = (strengths[key] || 0) + 1;
      });
      [...fb.weakest, ...directW].forEach((w) => {
        const key = typeof w === 'string' ? w : w?.title;
        if (!key) return;
        weakest[key] = (weakest[key] || 0) + 1;
      });
    });

    const top = (obj) =>
      Object.entries(obj)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([k, v]) => ({ label: k, count: v }));

    return { strengths: top(strengths), weakest: top(weakest) };
  }, [completed]);

  const radarData = useMemo(() => {
    const preferred = [
      'technical',
      'technical_skills',
      'communication',
      'problem_solving',
      'behavioral',
      'cultural_fit',
    ];
    const asMap = new Map((skillAnalysis || []).map((s) => [s.key, s]));
    const out = [];
    preferred.forEach((k) => {
      const row = asMap.get(k);
      if (!row) return;
      // dedupe by skill label
      if (out.some((x) => x.skill === row.skill)) return;
      out.push({ skill: row.skill, score: safeNum(row.score, 0) });
    });
    if (!out.length) {
      (skillAnalysis || []).slice(0, 6).forEach((s) =>
        out.push({ skill: s.skill, score: safeNum(s.score, 0) })
      );
    }
    return out;
  }, [skillAnalysis]);

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
    } catch {
      return String(d);
    }
  };

  const fmtTime = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const gradeFromScore = (s) => {
    if (s == null) return '—';
    if (s >= 95) return 'A+';
    if (s >= 90) return 'A';
    if (s >= 85) return 'A-';
    if (s >= 80) return 'B+';
    if (s >= 75) return 'B';
    if (s >= 70) return 'B-';
    if (s >= 65) return 'C+';
    if (s >= 60) return 'C';
    if (s >= 55) return 'C-';
    if (s >= 50) return 'D';
    return 'F';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-2xl border border-white/10 bg-[#080a0f]/90 px-4 py-3 shadow-2xl backdrop-blur-xl">
        {label != null ? (
          <div className="text-sm font-semibold text-white mb-2">{label}</div>
        ) : null}
        <div className="space-y-1">
          {payload.map((p, i) => (
            <div
              key={i}
              className="flex items-center gap-3 text-sm justify-between"
            >
              <span className="inline-flex items-center gap-2 text-white/70">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: p.color || p.fill || p.stroke }}
                />
                {p.name}
              </span>
              <span className="text-white font-semibold tabular-nums">
                {p.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleDelete = async () => {
    if (!selected || typeof onDeleteSession !== 'function') return;
    const ok = window.confirm(
      'Delete this interview session? This cannot be undone.'
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await onDeleteSession(selected.id);
      setSelected(null);
    } catch (e) {
      // parent already alerts; ignore here
    } finally {
      setDeleting(false);
    }
  };

  const statusPillTone = (st) => {
    switch (String(st || '').toLowerCase()) {
      case 'completed':
        return 'bg-emerald-500/15 text-emerald-200 ring-emerald-500/25';
      case 'in_progress':
        return 'bg-sky-500/15 text-sky-200 ring-sky-500/25';
      case 'cancelled':
        return 'bg-zinc-500/15 text-zinc-200 ring-zinc-500/25';
      default:
        return 'bg-amber-500/15 text-amber-200 ring-amber-500/25';
    }
  };

  // -------------------------------------------------------------------
  // Selected modal helpers — derive the same metric set PastInterviews showed
  // -------------------------------------------------------------------
  const selectedMeta = selected ? meta(selected) : null;
  const selectedSignals = selected ? readSignals(selected) : null;
  const selectedSuggestions = selected ? readSuggestions(selected) : [];
  const selectedScore = selected ? safeScore(selected) : null;
  const selectedFb = selected
    ? parseFeedback(selected?.feedback ?? selected?.results?.feedback)
    : null;
  const selectedStrengths = selected
    ? normalizeArray(
        selected?.strengths ?? selected?.results?.strengths
      ).concat(selectedFb?.strengths || [])
    : [];
  const selectedWeakest = selected
    ? normalizeArray(
        selected?.weakest_areas ?? selected?.results?.weakest_areas
      ).concat(selectedFb?.weakest || [])
    : [];
  const selectedCategories = selected
    ? selected?.performance_categories ??
      selected?.results?.performance_categories ??
      null
    : null;

  return (
    <>
      <SpotlightCard
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
        spotlightColor="rgba(56,189,248,0.14)"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-28 -left-28 h-[380px] w-[380px] rounded-full bg-sky-500/12 blur-[95px]" />
          <div className="absolute -bottom-28 -right-28 h-[380px] w-[380px] rounded-full bg-violet-500/10 blur-[95px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
        </div>

        <div className="relative z-10 p-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-white">
                Deep breakdown
              </div>
              <div className="text-xs text-white/55 mt-1">
                Drill into patterns by session, score distribution, format mix,
                and skill signals.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'sessions', label: 'Sessions', icon: 'ClipboardList' },
                { key: 'patterns', label: 'Patterns', icon: 'PieChart' },
                { key: 'signals', label: 'Signals', icon: 'Sparkles' },
              ].map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    aria-pressed={active}
                    className={[
                      'inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition',
                      active
                        ? 'border-white/14 bg-white/[0.10] text-white'
                        : 'border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.06]',
                    ].join(' ')}
                  >
                    <Icon
                      name={t.icon}
                      size={14}
                      className={active ? 'text-white' : 'text-white/70'}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* TAB: Sessions */}
          {tab === 'sessions' ? (
            <div className="mt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 w-full md:max-w-2xl">
                  <div className="relative flex-1 min-w-[220px]">
                    <Icon
                      name="Search"
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45"
                    />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search role, industry, persona, format…"
                      aria-label="Search sessions"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.03] px-10 py-2.5 text-sm text-white/85 outline-none focus:border-white/20"
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Status filter"
                    className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white/80 outline-none focus:border-white/20"
                  >
                    <option className="bg-[#0b0f16]" value="all">
                      All statuses
                    </option>
                    <option className="bg-[#0b0f16]" value="completed">
                      Completed
                    </option>
                    <option className="bg-[#0b0f16]" value="in_progress">
                      In progress
                    </option>
                    <option className="bg-[#0b0f16]" value="configured">
                      Configured
                    </option>
                    <option className="bg-[#0b0f16]" value="cancelled">
                      Cancelled
                    </option>
                  </select>

                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    aria-label="Format filter"
                    className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white/80 outline-none focus:border-white/20"
                  >
                    {typeOptions.map((t) => (
                      <option key={t} value={t} className="bg-[#0b0f16]">
                        {t === 'all' ? 'All formats' : prettyLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 text-xs text-white/55 flex-wrap">
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 tabular-nums">
                    {filtered.length} sessions
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 tabular-nums">
                    Avg {Math.round(safeNum(statistics?.average_score, 0))}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 tabular-nums">
                    Best {Math.round(safeNum(statistics?.highest_score, 0))}
                  </span>
                </div>
              </div>

              <div className="mt-5 overflow-hidden rounded-3xl border border-white/10">
                {/* Header row — hidden on mobile (cards stack) */}
                <div className="hidden md:grid grid-cols-12 bg-white/[0.03] px-4 py-3 text-[11px] text-white/60">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-3">Role</div>
                  <div className="col-span-2">Format</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2 text-right">Score</div>
                </div>

                <div className="max-h-[480px] overflow-y-auto bg-[#070a10]/40">
                  {filtered.length ? (
                    filtered
                      .slice()
                      .sort(
                        (a, b) =>
                          new Date(
                            b.completed_at || b.created_at || 0
                          ).getTime() -
                          new Date(
                            a.completed_at || a.created_at || 0
                          ).getTime()
                      )
                      .map((it) => {
                        const m = meta(it);
                        const score = safeScore(it);
                        const st = String(it?.status || '').toLowerCase();
                        const when = it.completed_at || it.created_at;
                        const isActive =
                          selected && selected.id === it.id;
                        return (
                          <button
                            key={
                              it.id ||
                              it.session_id ||
                              String(when)
                            }
                            type="button"
                            onClick={() => setSelected(it)}
                            aria-pressed={isActive}
                            className={[
                              'relative w-full text-left px-4 py-3 text-sm border-t border-white/8 transition',
                              'md:grid md:grid-cols-12 md:items-center',
                              'flex flex-col gap-1.5',
                              isActive
                                ? 'bg-sky-500/[0.08] ring-1 ring-inset ring-sky-400/30'
                                : 'hover:bg-white/[0.04]',
                            ].join(' ')}
                          >
                            {isActive ? (
                              <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-gradient-to-b from-sky-400 to-violet-400" />
                            ) : null}
                            <div className="md:col-span-3 text-white/75">
                              <span className="md:hidden text-[11px] text-white/45 mr-2">
                                Date:
                              </span>
                              {fmtDate(when)}{' '}
                              <span className="text-white/40">
                                {fmtTime(when)}
                              </span>
                            </div>
                            <div className="md:col-span-3 text-white/85 truncate">
                              <span className="md:hidden text-[11px] text-white/45 mr-2">
                                Role:
                              </span>
                              {m.role || '—'}
                            </div>
                            <div className="md:col-span-2 text-white/65">
                              <span className="md:hidden text-[11px] text-white/45 mr-2">
                                Format:
                              </span>
                              {prettyLabel(m.format || m.type)}
                            </div>
                            <div className="md:col-span-2">
                              <span
                                className={[
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ring-1',
                                  statusPillTone(st),
                                ].join(' ')}
                              >
                                {prettyLabel(st || 'configured')}
                              </span>
                            </div>
                            <div className="md:col-span-2 md:text-right">
                              <span className="inline-flex items-center md:justify-end gap-2">
                                <span
                                  className={[
                                    'h-2 w-2 rounded-full',
                                    score == null
                                      ? 'bg-white/30'
                                      : score >= 90
                                        ? 'bg-emerald-300'
                                        : score >= 80
                                          ? 'bg-sky-300'
                                          : score >= 70
                                            ? 'bg-violet-300'
                                            : 'bg-amber-300',
                                  ].join(' ')}
                                />
                                <span className="font-semibold text-white tabular-nums">
                                  {score == null ? '—' : Math.round(score)}
                                </span>
                              </span>
                            </div>
                          </button>
                        );
                      })
                  ) : (
                    <div className="px-4 py-10 text-center text-sm text-white/55">
                      No sessions match this filter yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* TAB: Patterns */}
          {tab === 'patterns' ? (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Score distribution
                  </div>
                  <div className="text-xs text-white/55 tabular-nums">
                    {completed.length} sessions
                  </div>
                </div>
                <div className="mt-4 h-[220px]">
                  {completed.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={scoreHistogram}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(255,255,255,0.06)"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="bucket"
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                          allowDecimals={false}
                          width={28}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        />
                        <Bar
                          dataKey="count"
                          name="Sessions"
                          radius={[8, 8, 0, 0]}
                          isAnimationActive={false}
                        >
                          {scoreHistogram.map((_, i) => (
                            <Cell
                              key={i}
                              fill={[
                                'rgba(244,63,94,0.55)',
                                'rgba(245,158,11,0.55)',
                                'rgba(167,139,250,0.55)',
                                'rgba(56,189,248,0.55)',
                                'rgba(52,211,153,0.55)',
                              ][i]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-white/50">
                      Complete sessions to see distribution.
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-white/55">
                  Goal: shift your distribution right by stabilizing structure
                  + timing.
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Average score by format
                  </div>
                  <div className="text-xs text-white/55">
                    Where you perform best
                  </div>
                </div>
                <div className="mt-4 h-[220px]">
                  {byTypeScore.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={byTypeScore}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(255,255,255,0.06)"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="type"
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                          interval={0}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                          width={28}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        />
                        <Bar
                          dataKey="avg"
                          name="Avg score"
                          radius={[8, 8, 0, 0]}
                          isAnimationActive={false}
                        >
                          {byTypeScore.map((_, i) => (
                            <Cell
                              key={i}
                              fill={palette[i % palette.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-white/50">
                      Complete sessions to see your format strengths.
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-white/55">
                  Tip: train in your weakest format to reduce performance
                  variance.
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Format mix
                  </div>
                  <div className="text-xs text-white/55">
                    Practice diversity
                  </div>
                </div>
                <div className="mt-4 h-[220px]">
                  {(typeDistribution || []).length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={typeDistribution || []}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}
                          isAnimationActive={false}
                        >
                          {(typeDistribution || []).map((_, i) => (
                            <Cell key={i} fill={palette[i % palette.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          wrapperStyle={{
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: 11,
                          }}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-white/50">
                      No format mix yet.
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-white/55">
                  More variety → better adaptability under real interview
                  pressure.
                </div>
              </div>

              <div className="lg:col-span-3 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Time-of-day signal
                  </div>
                  <div className="text-xs text-white/55">
                    Average score by time bucket
                  </div>
                </div>
                <div className="mt-4 h-[220px]">
                  {timeOfDayClean.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={timeOfDayClean}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(255,255,255,0.06)"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="bucket"
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.10)' }}
                          tickLine={false}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        />
                        <Bar
                          dataKey="score"
                          name="Avg score"
                          fill="rgba(52,211,153,0.55)"
                          radius={[10, 10, 0, 0]}
                          isAnimationActive={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-white/50">
                      Need more sessions to detect time-of-day patterns.
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-white/55">
                  Schedule hard practice in your best-performing time window.
                </div>
              </div>
            </div>
          ) : null}

          {/* TAB: Signals */}
          {tab === 'signals' ? (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Skills radar
                  </div>
                  <div className="text-xs text-white/55">
                    What your sessions are measuring
                  </div>
                </div>

                <div className="mt-4 h-[260px]">
                  {radarData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="78%">
                        <PolarGrid stroke="rgba(255,255,255,0.08)" />
                        <PolarAngleAxis
                          dataKey="skill"
                          tick={{
                            fill: 'rgba(255,255,255,0.65)',
                            fontSize: 11,
                          }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 100]}
                          tick={{
                            fill: 'rgba(255,255,255,0.35)',
                            fontSize: 10,
                          }}
                          axisLine={false}
                        />
                        <Radar
                          dataKey="score"
                          name="Score"
                          stroke="rgba(56,189,248,0.95)"
                          fill="rgba(56,189,248,0.22)"
                          isAnimationActive={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-white/50">
                      Skills will populate as your sessions report category
                      scores.
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(skillAnalysis || []).slice(0, 6).map((s, i) => (
                    <div
                      key={s.key || i}
                      className="rounded-2xl border border-white/10 bg-white/[0.02] p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-white/80 truncate">
                          {s.skill}
                        </div>
                        <div className="text-xs text-white/60 tabular-nums">
                          {Math.round(safeNum(s.score, 0))}
                        </div>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-400/70 via-sky-400/70 to-emerald-400/70"
                          style={{
                            width: `${Math.min(100, Math.max(0, safeNum(s.score, 0)))}%`,
                          }}
                        />
                      </div>
                      <div className="mt-2 text-[11px] text-white/55">
                        Trend:{' '}
                        <span
                          className={
                            safeNum(s.trend, 0) >= 0
                              ? 'text-emerald-300'
                              : 'text-rose-300'
                          }
                        >
                          {safeNum(s.trend, 0) >= 0 ? '+' : ''}
                          {Math.round(safeNum(s.trend, 0) * 10) / 10}
                        </span>
                        <span className="text-white/45">
                          {' '}
                          • {safeNum(s.count, 0)} sessions
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-1 space-y-6">
                <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-sm font-semibold text-white">
                    Strength signals
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    From your last 10 completed sessions.
                  </div>

                  <div className="mt-4">
                    <div className="text-xs text-white/60 font-semibold">
                      Top strengths
                    </div>
                    <div className="mt-2 space-y-2">
                      {topSignals.strengths.length ? (
                        topSignals.strengths.map((s) => (
                          <div
                            key={s.label}
                            className="flex items-center justify-between text-xs"
                          >
                            <span className="text-white/75 truncate">
                              {s.label}
                            </span>
                            <span className="text-white/55 tabular-nums">
                              {s.count}×
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-white/45">
                          Not enough feedback yet.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-white/10">
                    <div className="text-xs text-white/60 font-semibold">
                      Growth areas
                    </div>
                    <div className="mt-2 space-y-2">
                      {topSignals.weakest.length ? (
                        topSignals.weakest.map((w) => (
                          <div
                            key={w.label}
                            className="flex items-center justify-between text-xs"
                          >
                            <span className="text-white/75 truncate">
                              {w.label}
                            </span>
                            <span className="text-white/55 tabular-nums">
                              {w.count}×
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-white/45">
                          Not enough feedback yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-sm font-semibold text-white">
                    Recommended format
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    Train in the format you score lowest to reduce variance.
                  </div>
                  <div className="mt-4 space-y-2">
                    {byTypeScore
                      .slice()
                      .sort((a, b) => a.avg - b.avg)
                      .slice(0, 2)
                      .map((t) => (
                        <div
                          key={t.type}
                          className="flex items-center justify-between text-xs"
                        >
                          <span className="text-white/75">{t.type}</span>
                          <span className="text-white font-semibold tabular-nums">
                            {t.avg || 0}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </SpotlightCard>

      {/* Session detail drawer — PastInterviews-grade */}
      <Modal open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected ? (
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={[
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ring-1',
                      statusPillTone(selected.status),
                    ].join(' ')}
                  >
                    {prettyLabel(selected.status || 'configured')}
                  </span>
                  <span className="text-xs text-white/50">
                    Session #{selected.id}
                  </span>
                  <span className="text-xs text-white/35">•</span>
                  <span className="text-xs text-white/55">
                    {prettyLabel(selectedMeta?.format || selectedMeta?.type)}
                  </span>
                </div>

                <div className="mt-2 text-xl font-semibold text-white truncate">
                  {selectedMeta?.role || 'Untitled role'}
                </div>
                <div className="mt-1 text-sm text-white/60 truncate">
                  {selectedMeta?.industry || '—'}
                  {selectedMeta?.company_type
                    ? ` • ${selectedMeta.company_type}`
                    : ''}
                </div>

                <div className="mt-2 text-xs text-white/45 leading-relaxed">
                  Created: {fmtDate(selected.created_at)} {fmtTime(selected.created_at)}
                  <br />
                  Completed: {fmtDate(selected.completed_at)}{' '}
                  {fmtTime(selected.completed_at)}
                </div>
              </div>

              <div className="shrink-0 flex flex-col items-end gap-2">
                <Button
                  onClick={() => setSelected(null)}
                  className="!rounded-2xl !bg-white/[0.06] hover:!bg-white/[0.10] !border !border-white/10 !text-white/90"
                  aria-label="Close"
                >
                  <Icon name="X" size={16} />
                </Button>
                <div className="text-right">
                  <div className="text-xs text-white/55">Score</div>
                  <div className="text-3xl font-semibold text-white tabular-nums">
                    {selectedScore == null ? '—' : Math.round(selectedScore)}
                  </div>
                  <div className="text-xs text-white/45">
                    Grade: {gradeFromScore(selectedScore)}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick toggles / features */}
            <div className="mt-5 flex flex-wrap gap-2">
              {selectedMeta?.persona ? (
                <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70 inline-flex items-center gap-1">
                  <Icon name="Sparkles" size={12} />
                  {selectedMeta.persona}
                </span>
              ) : null}
              <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                Duration: {selectedMeta?.duration ?? '—'}m
              </span>
              <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                Difficulty:{' '}
                {prettyLabel(selectedMeta?.difficulty) || '—'}
              </span>
              <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                Tone: {prettyLabel(selectedMeta?.tone) || '—'}
              </span>
              {selectedMeta?.followups ? (
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/25 text-emerald-200">
                  Follow-ups enabled
                </span>
              ) : null}
              {selectedMeta?.star_enforced ? (
                <span className="text-xs px-2 py-1 rounded-full bg-violet-500/15 ring-1 ring-violet-500/25 text-violet-200">
                  STAR enforced
                </span>
              ) : null}
            </div>

            {/* Performance signals (PastInterviews-grade detail) */}
            <div className="mt-6">
              <div className="text-sm font-semibold text-white">
                Performance signals
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <MetricMini
                  label="Speaking pace"
                  value={selectedSignals?.speaking_wpm}
                  suffix=" WPM"
                  goodFrom={120}
                />
                <MetricMini
                  label="Filler words"
                  value={selectedSignals?.filler_words}
                  suffix=""
                  goodFrom={0}
                />
                <MetricMini
                  label="Eye contact"
                  value={selectedSignals?.eye_contact}
                  suffix="%"
                  goodFrom={70}
                />
                <MetricMini
                  label="STAR usage"
                  value={selectedSignals?.star_used}
                  suffix=""
                  goodFrom={1}
                />
                <MetricMini
                  label="Questions asked"
                  value={selectedSignals?.total_questions}
                  suffix=""
                  goodFrom={5}
                />
                <MetricMini
                  label="Follow-up intensity"
                  value={selectedMeta?.follow_intensity}
                  suffix="/5"
                  goodFrom={3}
                />
              </div>
              <div className="mt-2 text-[11px] text-white/45">
                * Some metrics appear only for completed sessions that
                submitted results.
              </div>
            </div>

            {/* Category breakdown */}
            {selectedCategories &&
            typeof selectedCategories === 'object' ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-sm font-semibold text-white">
                  Category breakdown
                </div>
                <div className="text-xs text-white/55 mt-1">
                  How your performance was measured in this session.
                </div>

                <div className="mt-4 space-y-2">
                  {Object.entries(selectedCategories)
                    .slice(0, 8)
                    .map(([k, v]) => {
                      const score =
                        typeof v === 'number'
                          ? v
                          : safeNum(v?.score ?? v?.value ?? v?.percentage, 0);
                      return (
                        <div
                          key={k}
                          className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-white/70 truncate">
                              {prettyLabel(k)}
                            </div>
                            <div className="text-xs text-white/60 tabular-nums shrink-0">
                              {Number.isFinite(score)
                                ? Math.round(score)
                                : '—'}
                            </div>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-violet-400/70 via-sky-400/70 to-emerald-400/70"
                              style={{
                                width: `${Math.min(100, Math.max(0, score))}%`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : null}

            {/* Strengths / Weakest areas */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-xs font-semibold text-white/75">
                  Strengths
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedStrengths.length ? (
                    selectedStrengths.slice(0, 8).map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/25 text-emerald-200"
                      >
                        {typeof s === 'string'
                          ? s
                          : s?.title || 'Strength'}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-white/45">—</span>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-xs font-semibold text-white/75">
                  Growth areas
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedWeakest.length ? (
                    selectedWeakest.slice(0, 8).map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded-full bg-amber-500/15 ring-1 ring-amber-500/25 text-amber-200"
                      >
                        {typeof s === 'string' ? s : s?.title || 'Area'}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-white/45">—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Recommendations (from PastInterviews) */}
            {selectedSuggestions.length ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-sm font-semibold text-white">
                  Recommendations
                </div>
                <div className="mt-3 space-y-2">
                  {selectedSuggestions.slice(0, 6).map((x, i) => (
                    <div
                      key={i}
                      className="text-sm text-white/75 leading-relaxed"
                    >
                      •{' '}
                      {typeof x === 'string'
                        ? x
                        : x?.action || x?.title || 'Recommendation'}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* AI feedback */}
            {selectedFb &&
            (selectedFb.general || selectedFb.detailed) ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-sm font-semibold text-white">
                  AI feedback
                </div>
                <div className="text-xs text-white/55 mt-1">
                  What you did well + what to fix next.
                </div>

                {selectedFb.general ? (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wide text-white/45">
                      General
                    </div>
                    <div className="mt-1 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">
                      {selectedFb.general}
                    </div>
                  </div>
                ) : null}

                {selectedFb.detailed ? (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wide text-white/45">
                      Detailed
                    </div>
                    <div className="mt-1 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">
                      {selectedFb.detailed}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Actions: delete */}
            {typeof onDeleteSession === 'function' ? (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="!rounded-2xl !bg-rose-500/15 hover:!bg-rose-500/25 !border !border-rose-500/30 !text-rose-100"
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon
                      name={deleting ? 'Loader2' : 'Trash2'}
                      size={16}
                      className={deleting ? 'animate-spin' : ''}
                    />
                    {deleting ? 'Deleting…' : 'Delete session'}
                  </span>
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default DetailedBreakdown;
